import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import DoisFatores from "../components/DoisFatores";
import EstadoCarregando from "../components/ui/EstadoCarregando";

const ESCALA_LABEL = {
  "12x36": "12x36",
  seg_sex: "Segunda a sexta",
  seg_sab: "Segunda a sábado",
};

function formatarData(data) {
  if (!data) return "—";
  return new Date(data + "T12:00").toLocaleDateString("pt-BR");
}

function Campo({ label, valor }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: COLORS.text, fontWeight: 600 }}>{valor || "—"}</div>
    </div>
  );
}

export default function MeusDados() {
  const { token } = useAuth();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [senhaAtual, setSenhaAtual] = useState("");
  const [senhaNova, setSenhaNova] = useState("");
  const [senhaConfirma, setSenhaConfirma] = useState("");
  const [erroSenha, setErroSenha] = useState("");
  const [sucessoSenha, setSucessoSenha] = useState("");
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const data = await apiFetch("GET", "/api/ponto/meus-dados", undefined, token);
      setDados(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const trocarSenha = async () => {
    setErroSenha("");
    setSucessoSenha("");
    if (senhaNova !== senhaConfirma) {
      setErroSenha("A confirmação não bate com a nova senha.");
      return;
    }
    setSalvandoSenha(true);
    try {
      await apiFetch("POST", "/api/auth/trocar-minha-senha", { senha_atual: senhaAtual, senha_nova: senhaNova }, token);
      setSenhaAtual("");
      setSenhaNova("");
      setSenhaConfirma("");
      setSucessoSenha("Senha alterada com sucesso.");
    } catch (e) {
      setErroSenha(e.message);
    } finally {
      setSalvandoSenha(false);
    }
  };

  if (loading) {
    return <EstadoCarregando padding={0} />;
  }

  if (erro) {
    return <Card><p style={{ color: COLORS.danger, fontSize: 13.5, margin: 0 }}>{erro}</p></Card>;
  }

  const f = dados?.funcionario;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Meus Dados
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Suas informações de cadastro e acesso.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 620 }}>
        {!f ? (
          <Card>
            <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
              Seu login ainda não está vinculado a um cadastro de funcionário.
            </p>
          </Card>
        ) : (
          <Card>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 16px" }}>Cadastro</h2>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "-10px 0 16px" }}>
              Esses dados são controlados pelo seu gestor — fale com ele se algo estiver errado.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
              <Campo label="Nome" valor={f.nome} />
              <Campo label="Cargo" valor={f.cargo} />
              <Campo label="CPF" valor={f.cpf} />
              <Campo label="RG" valor={f.rg} />
              <Campo label="Data de nascimento" valor={formatarData(f.data_nascimento)} />
              <Campo label="PIS" valor={f.pis} />
              <Campo label="Matrícula" valor={f.matricula} />
              <Campo label="Data de admissão" valor={formatarData(f.data_admissao)} />
              <Campo label="Posto" valor={f.posto_nome} />
              <Campo label="Escala" valor={ESCALA_LABEL[f.escala_tipo] || "Não definida"} />
              <Campo label="Entrada" valor={f.horario_entrada?.slice(0, 5)} />
              <Campo label="Saída" valor={f.horario_saida?.slice(0, 5)} />
            </div>
          </Card>
        )}

        {f && (
          <Card>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>Contato</h2>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 16px" }}>
              Esses dados são controlados pelo seu gestor — fale com ele se algo estiver errado.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
              <Campo label="Telefone" valor={f.telefone} />
              <Campo label="E-mail de contato" valor={f.email} />
            </div>
          </Card>
        )}

        <Card>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: "0 0 4px" }}>Acesso</h2>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 16px" }}>
            Login: <strong style={{ color: COLORS.textMid }}>{dados?.email_login}</strong>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input
              label="Senha atual"
              type="password"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              placeholder="••••••••"
            />
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 180px" }}>
                <Input
                  label="Nova senha"
                  type="password"
                  value={senhaNova}
                  onChange={(e) => setSenhaNova(e.target.value)}
                  placeholder="mínimo 6 caracteres"
                />
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <Input
                  label="Confirmar nova senha"
                  type="password"
                  value={senhaConfirma}
                  onChange={(e) => setSenhaConfirma(e.target.value)}
                  placeholder="repita a nova senha"
                />
              </div>
            </div>
            {erroSenha && (
              <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: RADIUS.sm, padding: "9px 13px" }}>
                {erroSenha}
              </div>
            )}
            {sucessoSenha && (
              <div style={{ color: COLORS.success, fontSize: 13, background: COLORS.successBg, border: "1px solid #BBF7D0", borderRadius: RADIUS.sm, padding: "9px 13px" }}>
                {sucessoSenha}
              </div>
            )}
            <Btn
              onClick={trocarSenha}
              disabled={salvandoSenha || !senhaAtual || !senhaNova || !senhaConfirma}
              style={{ alignSelf: "flex-start" }}
            >
              {salvandoSenha ? "Salvando..." : "Trocar senha"}
            </Btn>
          </div>
        </Card>

        <DoisFatores />
      </div>
    </div>
  );
}
